import { TFunction } from 'i18next'

import { TimelineDataType, TimelineDataTypes } from '@forto-dev/phoenix-ui-core/ship'
import { CarriageMode, TransportMode, TransportType } from '@freighthub/freighthub-corelib'
import { Point } from '@freighthub/freighthub-quotation-sdk'
import { CarriagePlan, TransportPlanEvent, TransportScheme } from '@freighthub/freighthub-transportplanning-sdk'

import { getAllEvents } from './transportPlanEventUtils'
import { getMilestoneEventCode } from './transportPlanMilestonesEventCodes'
import { getAllMilestones } from './transportPlanMilestoneUtils'
import { CarriagePlanType } from '../domain/carriagePlan'
import { MilestonesStep, TimelineStepsSequence } from '../domain/timelineTypes'
import { IMilestone, IMilestoneDelivery, IMilestoneDpr, IMilestones } from '../domain/transportPlanMilestones'

export type TimelineProgress = {
  event: TransportPlanEvent | undefined
  carriageType: CarriagePlanType
}

type TimelineStepRecord = {
  milestone?: IMilestone
  point?: Partial<Point>
  transportMode?: TransportMode | CarriageMode | null
}

export type TProgressTimelineMap = Partial<Record<MilestonesStep, TimelineProgress>>
export type TimelineRecord = Partial<Record<MilestonesStep, TimelineStepRecord>>

interface TimelineMilestones {
  pickup: IMilestone | undefined
  departure: IMilestone | undefined
  arrival: IMilestone | undefined
  dpr: IMilestoneDpr | undefined
  delivery: IMilestoneDelivery | undefined
  tsp1: Point | undefined
  tsp2: Point | undefined
  tsp3: Point | undefined
  tsp4: Point | undefined
}

export const getTimelineProgress = (
  carriageType: CarriagePlanType,
  event: TransportPlanEvent | undefined
): TimelineProgress => {
  return {
    event,
    carriageType,
  }
}

// for flex-grow
export const getMainStepsCount = (timelineSequence: TimelineProgress[]): number => {
  return timelineSequence.reduce((mainStepsCount, step): number => {
    return step.carriageType === 'main' ? mainStepsCount + 1 : mainStepsCount
  }, 0)
}

const getPickup = (milestones: IMilestones) => milestones.pickup
const getDeparture = (milestones: IMilestones) => milestones.pol || milestones.departureAirport || milestones.departure
const getArrival = (milestones: IMilestones) => milestones.pod || milestones.destinationAirport || milestones.arrival
const getDelivery = (milestones: IMilestones) => milestones.delivery
const getDpr = (milestones: IMilestones) => milestones.por

export const getTransshipmentPort = (plan: CarriagePlan | undefined, tspStepNumber: number): Point | undefined =>
  plan?.route?.segments && plan.route.segments.length > tspStepNumber
    ? plan.route.segments[tspStepNumber - 1].journey.destination
    : undefined

export const getTimelineMilestones = (milestones: IMilestones, main: CarriagePlan | undefined): TimelineMilestones => {
  const pickup = getPickup(milestones)
  const departure = getDeparture(milestones)
  const arrival = getArrival(milestones)
  const dpr = getDpr(milestones)
  const delivery = getDelivery(milestones)
  const tsp1 = getTransshipmentPort(main, 1)
  const tsp2 = getTransshipmentPort(main, 2)
  const tsp3 = getTransshipmentPort(main, 3)
  const tsp4 = getTransshipmentPort(main, 4)
  return { pickup, dpr, departure, arrival, delivery, tsp1, tsp2, tsp3, tsp4 }
}

export const getTimelineEvents = (
  timelineMilestones: TimelineMilestones,
  events: Record<string, TransportPlanEvent>,
  transportType: TransportType,
  transportMode: TransportMode
): Record<MilestonesStep, TransportPlanEvent | undefined> => {
  const TimeLineSteps: MilestonesStep[] = [
    MilestonesStep.CARGO_READY,
    MilestonesStep.PICKUP,
    MilestonesStep.DEPARTURE,
    MilestonesStep.ARRIVAL,
    MilestonesStep.DELIVERY,
  ]
  const [cargoReadyEventCode, pickupEventCode, departureEventCode, arrivalEventCode, deliveryEventCode] =
    TimeLineSteps.map((step) => {
      return getMilestoneEventCode(step, transportType, transportMode)
    })
  return {
    cargoReady: cargoReadyEventCode && events[cargoReadyEventCode],
    pickup: pickupEventCode && timelineMilestones.pickup && events[pickupEventCode],
    departure: departureEventCode && timelineMilestones.departure && events[departureEventCode],
    arrival: arrivalEventCode && timelineMilestones.arrival && events[arrivalEventCode],
    delivery: deliveryEventCode && timelineMilestones.delivery && events[deliveryEventCode],
    dpr: timelineMilestones.dpr && events.dpr,
    tsp1: timelineMilestones.tsp1 && events.vdt1,
    tsp2: timelineMilestones.tsp2 && events.vdt2,
    tsp3: timelineMilestones.tsp3 && events.vdt3,
    tsp4: timelineMilestones.tsp4 && events.vdt4,
    cgomd: events.cgomd,
    cgomo: events.cgomo,
  }
}

export const getContainersEvents = (tp: TransportScheme) => {
  const containerCount: number = tp.main ? tp.main.length : 0

  if (containerCount < 1) {
    return null
  }

  const containersEvents: {
    step: MilestonesStep
    event: TransportPlanEvent | undefined
  }[][] = tp.main.map((_, index) => {
    const pre = tp.pre?.[index]
    const main = tp.main?.[index]
    const on = tp.on?.[index]
    const milestones = getAllMilestones([pre, main, on])
    const timelineMilestones = getTimelineMilestones(milestones, main)
    const events: Record<string, TransportPlanEvent> = getAllEvents([pre, main, on])
    const timelineEvents: Record<MilestonesStep, TransportPlanEvent | undefined> = getTimelineEvents(
      timelineMilestones,
      events,
      tp.transportType,
      tp.transportMode
    )
    return TimelineStepsSequence.filter((step) => !step.match(/^tsp/)).map((step) => ({
      step: step as MilestonesStep,
      event: timelineEvents[step],
    }))
  })

  return containersEvents.reduce(
    (result, events) => {
      events.forEach(({ step, event }) => {
        if (!result[step]) {
          result[step] = []
        }
        if (event) {
          result[step].push(event)
        }
      })
      return result
    },
    {} as Record<MilestonesStep, TransportPlanEvent[]>
  )
}

export const getContainersCargoReady = (transportScheme: TransportScheme): (TransportPlanEvent | undefined)[] => {
  const crdEventName = getMilestoneEventCode(
    MilestonesStep.CARGO_READY,
    transportScheme.transportType,
    transportScheme.transportMode
  )
  if (!crdEventName) {
    return []
  }
  const crdEvents: (TransportPlanEvent | undefined)[] =
    transportScheme.origin?.map(({ events }) => events[crdEventName]) || []

  if (!crdEvents) {
    return []
  }

  return crdEvents?.filter(Boolean) ?? []
}

export interface TransportPlanInformation {
  timelineMap?: TProgressTimelineMap
  timelineSequence?: (TimelineProgress | undefined)[]
}

export const combineTimelineContainers = (
  timelineData: TimelineDataTypes[][],
  translate: TFunction<'common', undefined>
): TimelineDataTypes[][] => {
  if (timelineData.length <= 1) {
    // To avoid cases of [[]] and timeline UI displaying empty accordion
    if (timelineData.flatMap((i) => i).length === 0) {
      return []
    }
    return timelineData
  }
  const combinedData: TimelineDataTypes[][] = []
  const uniqueContainers = new Set<number>()
  const containersAmount = timelineData.length

  timelineData.forEach((currentData, currentDataIndex) => {
    if (uniqueContainers.has(currentDataIndex)) {
      return
    }
    let numberOfTheSameContainers = 0
    const containerIds: string[] = []
    timelineData.forEach((nextData, nextDataIndex) => {
      if (currentData.length === nextData.length) {
        let isEqual = true
        currentData.forEach((currentDataItem, currentDataItemIndex) => {
          if (
            'value' in currentDataItem &&
            'value' in nextData[currentDataItemIndex] &&
            currentDataItem.value !== nextData[currentDataItemIndex].value &&
            currentDataItemIndex !== 0
          ) {
            isEqual = false
            return
          }
        })
        if (isEqual) {
          numberOfTheSameContainers++
          if ('value' in nextData[0]) {
            containerIds.push(nextData[0].value)
          }
          uniqueContainers.add(nextDataIndex)
        }
      }
    })

    if (numberOfTheSameContainers > 1) {
      const value =
        containersAmount !== numberOfTheSameContainers
          ? containerIds.join(', ')
          : translate('shipment.timeline.containers_all', { length: numberOfTheSameContainers })
      combinedData.push([
        {
          type: TimelineDataType.LabelValue,
          label: translate('shipment.timeline.containers'),
          value,
          truncate: true,
        },
        ...currentData.slice(1),
      ])
    } else {
      combinedData.push(currentData)
    }
  })
  return combinedData
}
